import { 
  SetRange, 
  RepRange, 
  RestRange, 
  ExerciseRecord, 
  WorkoutSession,
  RPE,
  UserProgress,
  Exercise,
  CurrentWorkoutState
} from '../types/workout';

export interface WarmupSetInfo {
  reps: string;
  weightPercentage: string;
}

export interface DailyProgress {
  completed: number;
  total: number;
}

export interface WeeklyDetails {
  completedDays: number;
  totalDays: number;
  dayStatus: { [key: string]: boolean | 'skipped' };
}

export class WorkoutUtils {
  /**
   * Parse set range from string like "2-3" or "1"
   */
  static parseSetRange(sets: string): SetRange {
    const trimmed = sets.trim();
    if (trimmed.includes('-')) {
      const [min, max] = trimmed.split('-').map(s => parseInt(s.trim()));
      return { min, max };
    }
    const value = parseInt(trimmed);
    return { min: value, max: value };
  }

  /**
   * Parse rep range from string like "6-8" or "10"
   */
  static parseRepRange(reps: string): RepRange {
    const trimmed = reps.trim();
    if (trimmed.includes('-')) {
      const [min, max] = trimmed.split('-').map(s => parseInt(s.trim()));
      return { min, max };
    }
    const value = parseInt(trimmed);
    return { min: value, max: value };
  }

  /**
   * Parse rest range from string like "3-5 min" or "1-2 min"
   */
  static parseRestRange(rest: string): RestRange {
    const trimmed = rest.trim().toLowerCase();
    const isMinutes = trimmed.includes('min');
    const unit = isMinutes ? 'min' : 'sec';
    
    // Remove unit text
    const numberPart = trimmed.replace(/\s*(min|sec|minute|second)s?\s*/g, '');
    
    if (numberPart.includes('-')) {
      const [min, max] = numberPart.split('-').map(s => parseInt(s.trim()));
      return { min, max, unit };
    }
    const value = parseInt(numberPart);
    return { min: value, max: value, unit };
  }

  /**
   * Calculate total volume (weight × reps × sets)
   */
  static calculateVolume(weight: number, reps: number, sets: number): number {
    return weight * reps * sets;
  }

  /**
   * Check if current exercise record is a personal record
   */
  static isPersonalRecord(current: ExerciseRecord, previousRecords: ExerciseRecord[]): boolean {
    const exerciseRecords = previousRecords.filter(
      record => record.exerciseName === current.exerciseName
    );

    if (exerciseRecords.length === 0) {
      return true; // First time doing this exercise
    }

    const currentMaxWeight = Math.max(...current.sets.map(set => set.weight));
    const currentMaxVolume = current.totalVolume;

    const previousMaxWeight = Math.max(
      ...exerciseRecords.flatMap(record => record.sets.map(set => set.weight))
    );
    const previousMaxVolume = Math.max(
      ...exerciseRecords.map(record => record.totalVolume)
    );

    return currentMaxWeight > previousMaxWeight || currentMaxVolume > previousMaxVolume;
  }

  /**
   * Generate a unique ID for workout sessions
   */
  static generateWorkoutId(): string {
    return `workout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format date to YYYY-MM-DD
   */
  static formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Get current week number based on start date
   */
  static getCurrentWeek(startDate: Date, currentDate: Date = new Date()): number {
    const diffTime = Math.abs(currentDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.min(Math.ceil(diffDays / 7), 12); // Max 12 weeks
  }

  /**
   * Get the next day in the weekly cycle
   */
  static getNextDay(currentDay: string): string {
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const currentIndex = weekDays.indexOf(currentDay);
    
    if (currentIndex === -1 || currentIndex === weekDays.length - 1) {
      return weekDays[0]; // Sunday -> Monday (next week)
    }
    
    return weekDays[currentIndex + 1];
  }

  /**
   * Get the next workout day (skipping rest days)
   */
  static getNextWorkoutDay(currentDay: string): string {
    const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday'];
    const currentIndex = workoutDays.indexOf(currentDay);
    
    if (currentIndex === -1 || currentIndex === workoutDays.length - 1) {
      return workoutDays[0]; // Start over with Monday
    }
    
    return workoutDays[currentIndex + 1];
  }

  /**
   * Check if a day is a rest day
   */
  static isRestDay(day: string): boolean {
    return ['Wednesday', 'Sunday'].includes(day);
  }

  /**
   * Check if progressing from current day should advance the week
   */
  static shouldAdvanceWeek(currentDay: string): boolean {
    return currentDay === 'Sunday'; // Sunday -> Monday (next week)
  }

  /**
   * Calculate workout completion percentage
   */
  static calculateWorkoutProgress(sessions: WorkoutSession[], totalWeeks: number = 12): number {
    const totalWorkouts = totalWeeks * 4; // 4 workout days per week
    const completedWorkouts = sessions.filter(session => session.completed).length;
    return Math.round((completedWorkouts / totalWorkouts) * 100);
  }

  /**
   * Get exercise statistics
   */
  static getExerciseStats(exerciseName: string, sessions: WorkoutSession[]) {
    const exerciseRecords = sessions
      .flatMap(session => session.exercises)
      .filter(exercise => exercise.exerciseName === exerciseName);

    if (exerciseRecords.length === 0) {
      return null;
    }

    const allSets = exerciseRecords.flatMap(record => record.sets);
    const maxWeight = Math.max(...allSets.map(set => set.weight));
    const totalVolume = exerciseRecords.reduce((sum, record) => sum + record.totalVolume, 0);
    const averageReps = allSets.reduce((sum, set) => sum + set.reps, 0) / allSets.length;

    return {
      maxWeight,
      totalVolume,
      averageReps: Math.round(averageReps * 10) / 10,
      timesPerformed: exerciseRecords.length,
      lastPerformed: exerciseRecords[exerciseRecords.length - 1]?.date
    };
  }

  /**
   * Validate RPE value
   */
  static isValidRPE(rpe: string): boolean {
    return Object.values(RPE).includes(rpe as RPE);
  }

  /**
   * Convert RPE string to number for calculations
   */
  static rpeToNumber(rpe: string): number {
    // Handle string format from exercises.json (e.g., "~7", "~8")
    if (typeof rpe === 'string') {
      const cleanRpe = rpe.replace('~', '');
      const numericRpe = parseFloat(cleanRpe);
      if (!isNaN(numericRpe)) {
        return numericRpe;
      }
    }
    
    switch (rpe) {
      case RPE.RPE_6: return 6;
      case RPE.RPE_7: return 7;
      case RPE.RPE_8: return 8;
      case RPE.RPE_9: return 9;
      case RPE.RPE_10: return 10;
      default: return 0;
    }
  }

  /**
   * Get workout type based on day
   */
  static getWorkoutType(day: string): string {
    switch (day) {
      case 'Monday':
        return 'Upper • Strength';
      case 'Tuesday':
        return 'Lower • Strength';
      case 'Thursday':
        return 'Pull • Hypertrophy';
      case 'Friday':
        return 'Push • Hypertrophy';
      case 'Saturday':
        return 'Legs • Hypertrophy';
      default:
        return 'Workout';
    }
  }

  /**
   * Get workout badge variant based on day
   */
  static getWorkoutBadgeVariant(day: string): 'push' | 'pull' | 'legs' | 'default' {
    switch (day) {
      case 'Monday':
        return 'default'; // Upper body (mixed)
      case 'Tuesday':
      case 'Saturday':
        return 'legs';
      case 'Thursday':
        return 'pull';
      case 'Friday':
        return 'push';
      default:
        return 'default';
    }
  }

  /**
   * Get warmup set information based on number of warmup sets
   */
  static getWarmupSetInfo(totalWarmupSets: number, setIndex: number): WarmupSetInfo {
    const warmupConfigs = {
      1: [
        { reps: "6-10", weightPercentage: "60%" }
      ],
      2: [
        { reps: "6-10", weightPercentage: "50%" },
        { reps: "4-6", weightPercentage: "70%" }
      ],
      3: [
        { reps: "6-10", weightPercentage: "45%" },
        { reps: "4-6", weightPercentage: "65%" },
        { reps: "3-4", weightPercentage: "85%" }
      ],
      4: [
        { reps: "6-10", weightPercentage: "45%" },
        { reps: "4-6", weightPercentage: "60%" },
        { reps: "3-5", weightPercentage: "75%" },
        { reps: "2-4", weightPercentage: "85%" }
      ]
    };

    const config = warmupConfigs[totalWarmupSets as keyof typeof warmupConfigs];
    if (!config || setIndex >= config.length) {
      // Fallback for unexpected cases
      return { reps: "6-10", weightPercentage: "60%" };
    }

    return config[setIndex];
  }

  /**
   * Get all days of the week
   */
  static getAllDays(): string[] {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  }

  /**
   * Get the workout days for the program (excluding rest days)
   */
  static getWorkoutDays(): string[] {
    return ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
  }

  /**
   * Calculate today's exercise progress
   */
  static calculateDailyProgress(
    currentExercises: Exercise[] | string,
    isExerciseCompleted: (index: number) => boolean
  ): DailyProgress {
    if (!Array.isArray(currentExercises)) {
      return { completed: 0, total: 0 };
    }
    
    const total = currentExercises.length;
    const completed = currentExercises.filter((_, index) => isExerciseCompleted(index)).length;
    
    return { completed, total };
  }

  /**
   * Calculate current week progress based on completed days
   */
  static calculateWeekProgress(
    userProgress: UserProgress | null,
    currentWeek: number,
    dailyProgress: DailyProgress
  ): number {
    if (!userProgress) return 0;
    
    const allDays = this.getAllDays();
    const workoutDays = this.getWorkoutDays();
    const currentDay = userProgress.currentDay;
    
    // Get all workout sessions for the current week (completed OR skipped)
    const weekSessions = userProgress.workoutHistory.filter(
      session => session.week === currentWeek
    );
    
    // Count completed and skipped workouts - make case insensitive and handle whitespace
    const completedWorkouts = weekSessions.filter(session => session.completed);
    const skippedWorkouts = weekSessions.filter(session => 
      !session.completed && session.notes && (session.notes.trim().toLowerCase() === 'skipped')
    );
    const attemptedWorkouts = [...completedWorkouts, ...skippedWorkouts];
    
    // Base progress from attempted workout days (completed + skipped)
    const attemptedWorkoutDays = workoutDays.filter(day => 
      attemptedWorkouts.some(session => session.day === day)
    ).length;
    
    // Check if today's workout is completed (for workout days)
    const isTodayWorkoutDay = workoutDays.includes(currentDay);
    const isTodayCompleted = isTodayWorkoutDay && 
      dailyProgress.total > 0 && 
      dailyProgress.completed === dailyProgress.total;
    
    // Adjust for current day completion
    let adjustedAttemptedWorkouts = attemptedWorkoutDays;
    if (isTodayCompleted) {
      const alreadyCounted = attemptedWorkouts.some(session => session.day === currentDay);
      if (!alreadyCounted) {
        adjustedAttemptedWorkouts += 1;
      }
    }
    
    // Calculate progress: (attempted_workouts / total_workouts) * 100
    const totalWorkoutDays = 5;
    const workoutProgress = (adjustedAttemptedWorkouts / totalWorkoutDays) * 100;
    
    return Math.round(Math.min(workoutProgress, 100));
  }

  /**
   * Get detailed weekly progress information
   */
  static getWeeklyDetails(
    userProgress: UserProgress | null,
    currentWeek: number,
    dailyProgress: DailyProgress
  ): WeeklyDetails {
    if (!userProgress) return { completedDays: 0, totalDays: 5, dayStatus: {} };
    
    const workoutDays = this.getWorkoutDays();
    
    // Get all workout sessions for the current week
    const weekSessions = userProgress.workoutHistory.filter(
      session => session.week === currentWeek
    );
    
    // Create status for each workout day
    const dayStatus: { [key: string]: boolean | 'skipped' } = {};
    workoutDays.forEach(day => {
      const daySession = weekSessions.find(session => session.day === day);
      if (daySession) {
        if (daySession.completed) {
          dayStatus[day] = true; // Completed
        } else if (daySession.notes && daySession.notes.trim().toLowerCase() === 'skipped') {
          dayStatus[day] = 'skipped'; // Skipped
        } else {
          dayStatus[day] = false; // In progress but not completed
        }
      } else {
        dayStatus[day] = false; // Not started
      }
    });
    
    // Check if today's workout is completed
    const currentDay = userProgress.currentDay;
    const isTodayCompleted = dailyProgress.total > 0 && dailyProgress.completed === dailyProgress.total;
    
    if (workoutDays.includes(currentDay) && isTodayCompleted && dayStatus[currentDay] === false) {
      dayStatus[currentDay] = true;
    }
    
    // Count completed and skipped days as "attempted"
    const attemptedDays = Object.values(dayStatus).filter(status => status === true || status === 'skipped').length;
    
    return { completedDays: attemptedDays, totalDays: 5, dayStatus };
  }

  /**
   * Calculate total training volume across all workouts
   */
  static calculateTotalVolume(sessions: WorkoutSession[]): number {
    return sessions.reduce((total, session) => {
      return total + session.exercises.reduce((sessionTotal, exercise) => {
        return sessionTotal + exercise.totalVolume;
      }, 0);
    }, 0);
  }

  /**
   * Calculate workout consistency percentage
   */
  static calculateConsistency(
    userProgress: UserProgress | null,
    weeks: number = 4
  ): number {
    if (!userProgress) return 0;
    
    const totalExpectedWorkouts = weeks * 5; // 5 workouts per week
    const completedWorkouts = userProgress.workoutHistory.filter(
      session => session.completed
    ).length;
    
    return Math.round((completedWorkouts / totalExpectedWorkouts) * 100);
  }

  /**
   * Get average RPE across all completed sets
   */
  static calculateAverageRPE(sessions: WorkoutSession[]): number {
    const allSets = sessions.flatMap(session => 
      session.exercises.flatMap(exercise => exercise.sets)
    );
    
    const rpeValues = allSets
      .map(set => set.rpe ? this.rpeToNumber(set.rpe) : 0)
      .filter(rpe => rpe > 0);
    
    if (rpeValues.length === 0) return 0;
    
    const average = rpeValues.reduce((sum, rpe) => sum + rpe, 0) / rpeValues.length;
    return Math.round(average * 10) / 10; // Round to 1 decimal place
  }

  /**
   * Get total number of sets completed
   */
  static getTotalSets(sessions: WorkoutSession[]): number {
    return sessions.reduce((total, session) => {
      return total + session.exercises.reduce((sessionTotal, exercise) => {
        return sessionTotal + exercise.sets.length;
      }, 0);
    }, 0);
  }

  /**
   * Get total number of reps completed
   */
  static getTotalReps(sessions: WorkoutSession[]): number {
    return sessions.reduce((total, session) => {
      return total + session.exercises.reduce((sessionTotal, exercise) => {
        return sessionTotal + exercise.sets.reduce((exerciseTotal, set) => {
          return exerciseTotal + set.reps;
        }, 0);
      }, 0);
    }, 0);
  }

  /**
   * Get personal records for major lifts
   */
  static getPersonalRecords(sessions: WorkoutSession[]): { [exerciseName: string]: number } {
    const exerciseMaxes: { [exerciseName: string]: number } = {};
    
    // Major compound movements to track
    const majorLifts = [
      'Bench Press',
      'Squat',
      'Deadlift',
      'Overhead Press',
      'Barbell Row',
      'Incline Bench Press'
    ];
    
    sessions.forEach(session => {
      session.exercises.forEach(exercise => {
        const exerciseName = exercise.exerciseName;
        
        // Only track major lifts
        const isTrackedLift = majorLifts.some(lift => 
          exerciseName.toLowerCase().includes(lift.toLowerCase())
        );
        
        if (isTrackedLift) {
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          
          if (!exerciseMaxes[exerciseName] || maxWeight > exerciseMaxes[exerciseName]) {
            exerciseMaxes[exerciseName] = maxWeight;
          }
        }
      });
    });
    
    return exerciseMaxes;
  }

  /**
   * Calculate program completion percentage
   */
  static calculateProgramProgress(currentWeek: number, totalWeeks: number = 12): number {
    return Math.round((currentWeek / totalWeeks) * 100);
  }

  /**
   * Get workout frequency for a given period
   */
  static getWorkoutFrequency(
    sessions: WorkoutSession[],
    weeks: number = 4
  ): number {
    const completedWorkouts = sessions.filter(session => session.completed).length;
    return Math.round((completedWorkouts / weeks) * 10) / 10; // Round to 1 decimal
  }

  /**
   * Calculate strength progress based on weight increases
   */
  static calculateStrengthProgress(
    sessions: WorkoutSession[],
    exerciseName: string
  ): number {
    const exerciseSessions = sessions
      .flatMap(session => session.exercises)
      .filter(exercise => exercise.exerciseName.toLowerCase().includes(exerciseName.toLowerCase()))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    if (exerciseSessions.length < 2) return 0;
    
    const firstMax = Math.max(...exerciseSessions[0].sets.map(set => set.weight));
    const lastMax = Math.max(...exerciseSessions[exerciseSessions.length - 1].sets.map(set => set.weight));
    
    return Math.round(((lastMax - firstMax) / firstMax) * 100);
  }

  /**
   * Get completed weeks with their workout sessions
   */
  static getCompletedWeeks(userProgress: UserProgress | null): { weekNumber: number; sessions: WorkoutSession[]; isCompleted: boolean }[] {
    if (!userProgress) return [];
    
    // Group sessions by week
    const sessionsByWeek: { [week: number]: WorkoutSession[] } = {};
    
    userProgress.workoutHistory.forEach(session => {
      if (!sessionsByWeek[session.week]) {
        sessionsByWeek[session.week] = [];
      }
      sessionsByWeek[session.week].push(session);
    });
    
    // Convert to array and sort by week number
    const weeks = Object.keys(sessionsByWeek)
      .map(weekStr => parseInt(weekStr))
      .sort((a, b) => a - b)
      .map(weekNumber => {
        const sessions = sessionsByWeek[weekNumber];
        const completedWorkouts = sessions.filter(session => session.completed).length;
        const totalWorkoutDays = 5; // Monday, Tuesday, Thursday, Friday, Saturday
        const isCompleted = completedWorkouts >= totalWorkoutDays;
        
        return {
          weekNumber,
          sessions,
          isCompleted
        };
      });
    
    return weeks;
  }

  /**
   * Get sessions for a specific week
   */
  static getWeekSessions(userProgress: UserProgress | null, weekNumber: number): WorkoutSession[] {
    if (!userProgress) return [];
    
    return userProgress.workoutHistory.filter(session => session.week === weekNumber);
  }

  /**
   * Compare today's weight vs same day last week
   */
  static getTodayVsLastWeekComparison(
    userProgress: UserProgress | null,
    currentDay: string
  ): { improvement: number; hasData: boolean; exerciseCount: number } {
    if (!userProgress) return { improvement: 0, hasData: false, exerciseCount: 0 };
    
    const currentWeek = userProgress.currentWeek;
    const lastWeek = currentWeek - 1;
    
    // Get sessions for current day this week and last week
    const thisWeekSession = userProgress.workoutHistory.find(
      session => session.week === currentWeek && session.day === currentDay && session.completed
    );
    const lastWeekSession = userProgress.workoutHistory.find(
      session => session.week === lastWeek && session.day === currentDay && session.completed
    );
    
    if (!thisWeekSession || !lastWeekSession) {
      return { improvement: 0, hasData: false, exerciseCount: 0 };
    }
    
    let totalImprovement = 0;
    let exerciseCount = 0;
    
    // Compare exercises that appear in both sessions
    thisWeekSession.exercises.forEach(thisWeekExercise => {
      const lastWeekExercise = lastWeekSession.exercises.find(
        ex => ex.exerciseName === thisWeekExercise.exerciseName
      );
      
      if (lastWeekExercise) {
        const thisWeekMax = Math.max(...thisWeekExercise.sets.map(set => set.weight));
        const lastWeekMax = Math.max(...lastWeekExercise.sets.map(set => set.weight));
        
        if (lastWeekMax > 0) {
          const improvement = ((thisWeekMax - lastWeekMax) / lastWeekMax) * 100;
          totalImprovement += improvement;
          exerciseCount++;
        }
      }
    });
    
    const averageImprovement = exerciseCount > 0 ? totalImprovement / exerciseCount : 0;
    
    return {
      improvement: Math.round(averageImprovement * 10) / 10,
      hasData: exerciseCount > 0,
      exerciseCount
    };
  }

  /**
   * Find the most improved exercise since the beginning
   */
  static getMostImprovedExercise(userProgress: UserProgress | null): {
    exerciseName: string;
    improvement: number;
    startWeight: number;
    currentWeight: number;
    hasData: boolean;
  } {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return { exerciseName: '', improvement: 0, startWeight: 0, currentWeight: 0, hasData: false };
    }
    
    // Group exercises by name and get first and last performances
    const exerciseProgress: { [name: string]: { first: number; last: number; sessions: number } } = {};
    
    userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(session => {
        session.exercises.forEach(exercise => {
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          
          if (!exerciseProgress[exercise.exerciseName]) {
            exerciseProgress[exercise.exerciseName] = {
              first: maxWeight,
              last: maxWeight,
              sessions: 1
            };
          } else {
            exerciseProgress[exercise.exerciseName].last = maxWeight;
            exerciseProgress[exercise.exerciseName].sessions++;
          }
        });
      });
    
    // Find exercise with highest improvement percentage
    let bestExercise = { exerciseName: '', improvement: 0, startWeight: 0, currentWeight: 0 };
    
    Object.entries(exerciseProgress).forEach(([exerciseName, data]) => {
      // Only consider exercises performed multiple times
      if (data.sessions >= 3 && data.first > 0) {
        const improvement = ((data.last - data.first) / data.first) * 100;
        
        if (improvement > bestExercise.improvement) {
          bestExercise = {
            exerciseName,
            improvement: Math.round(improvement * 10) / 10,
            startWeight: data.first,
            currentWeight: data.last
          };
        }
      }
    });
    
    return {
      ...bestExercise,
      hasData: bestExercise.exerciseName !== ''
    };
  }

  /**
   * Get highest single weight and highest total volume records
   */
  static getStrengthRecords(userProgress: UserProgress | null): {
    highestWeight: { weight: number; exerciseName: string; hasData: boolean };
    highestVolume: { volume: number; exerciseName: string; date: string; hasData: boolean };
  } {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return {
        highestWeight: { weight: 0, exerciseName: '', hasData: false },
        highestVolume: { volume: 0, exerciseName: '', date: '', hasData: false }
      };
    }
    
    let highestWeight = { weight: 0, exerciseName: '', hasData: false };
    let highestVolume = { volume: 0, exerciseName: '', date: '', hasData: false };
    
    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          // Check for highest single weight
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          if (maxWeight > highestWeight.weight) {
            highestWeight = {
              weight: maxWeight,
              exerciseName: exercise.exerciseName,
              hasData: true
            };
          }
          
          // Check for highest volume in a single exercise
          if (exercise.totalVolume > highestVolume.volume) {
            highestVolume = {
              volume: exercise.totalVolume,
              exerciseName: exercise.exerciseName,
              date: exercise.date,
              hasData: true
            };
          }
        });
      });
    
    return { highestWeight, highestVolume };
  }

  /**
   * Get overall training statistics
   */
  static getOverallStats(userProgress: UserProgress | null): {
    totalWorkouts: number;
    totalVolume: number;
    averageRPE: number;
    totalSets: number;
    totalReps: number;
    consistency: number;
    averageDuration: number;
  } {
    if (!userProgress) {
      return {
        totalWorkouts: 0,
        totalVolume: 0,
        averageRPE: 0,
        totalSets: 0,
        totalReps: 0,
        consistency: 0,
        averageDuration: 0
      };
    }
    
    const completedSessions = userProgress.workoutHistory.filter(session => session.completed);
    
    return {
      totalWorkouts: completedSessions.length,
      totalVolume: this.calculateTotalVolume(completedSessions),
      averageRPE: this.calculateAverageRPE(completedSessions),
      totalSets: this.getTotalSets(completedSessions),
      totalReps: this.getTotalReps(completedSessions),
      consistency: this.calculateConsistency(userProgress, 4),
      averageDuration: this.calculateAverageDuration(completedSessions)
    };
  }

  /**
   * Calculate average workout duration in minutes
   */
  static calculateAverageDuration(sessions: WorkoutSession[]): number {
    const sessionsWithDuration = sessions.filter(session => session.duration && session.duration > 0);
    
    if (sessionsWithDuration.length === 0) return 0;
    
    const totalDuration = sessionsWithDuration.reduce((sum, session) => sum + (session.duration || 0), 0);
    return Math.round(totalDuration / sessionsWithDuration.length);
  }

  /**
   * Get weekly RPE accuracy breakdown for all exercises
   */
  static getWeeklyRPEAccuracy(userProgress: UserProgress | null): Array<{
    week: number;
    accuracy: number;
    totalSets: number;
    averageExpectedRPE: number;
    averageActualRPE: number;
    exerciseCount: number;
  }> {
    if (!userProgress) return [];

    // Import exercises data to get expected RPE
    const exercisesData = require('../exercises.json');
    
    const weeklyData: { [week: number]: {
      totalAccuracy: number;
      setCount: number;
      expectedRPESum: number;
      actualRPESum: number;
      exercises: Set<string>;
    } } = {};

    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          // Find exercise definition
          let exerciseDefinition: any = null;
          Object.values(exercisesData).forEach((weekData: any) => {
            Object.values(weekData).forEach((dayExercises: any) => {
              if (Array.isArray(dayExercises)) {
                const found = dayExercises.find((ex: any) => ex.exercise === exercise.exerciseName);
                if (found) {
                  exerciseDefinition = found;
                }
              }
            });
          });

          if (!exerciseDefinition) return;

          const workingSets = exercise.sets.filter(set => 
            set.setType === 'working' && 
            set.completed && 
            set.actualRpe
          );

          workingSets.forEach((set, index) => {
            const isLastSet = index === workingSets.length - 1;
            const expectedRPEString = isLastSet 
              ? exerciseDefinition.last_set_rpe 
              : exerciseDefinition.early_set_rpe;

            if (expectedRPEString === "N/A") return;

            const expectedRPE = this.rpeToNumber(expectedRPEString);
            const actualRPE = this.rpeToNumber(set.actualRpe!);
            
            if (expectedRPE > 0 && actualRPE > 0) {
              if (!weeklyData[session.week]) {
                weeklyData[session.week] = {
                  totalAccuracy: 0,
                  setCount: 0,
                  expectedRPESum: 0,
                  actualRPESum: 0,
                  exercises: new Set()
                };
              }

              const accuracy = Math.max(0, 100 - Math.abs(actualRPE - expectedRPE) * 10);
              weeklyData[session.week].totalAccuracy += accuracy;
              weeklyData[session.week].setCount += 1;
              weeklyData[session.week].expectedRPESum += expectedRPE;
              weeklyData[session.week].actualRPESum += actualRPE;
              weeklyData[session.week].exercises.add(exercise.exerciseName);
            }
          });
        });
      });

    return Object.entries(weeklyData)
      .map(([week, data]) => ({
        week: parseInt(week),
        accuracy: Math.round(data.totalAccuracy / data.setCount),
        totalSets: data.setCount,
        averageExpectedRPE: Math.round((data.expectedRPESum / data.setCount) * 10) / 10,
        averageActualRPE: Math.round((data.actualRPESum / data.setCount) * 10) / 10,
        exerciseCount: data.exercises.size
      }))
      .sort((a, b) => a.week - b.week);
  }

  /**
   * Get workout duration trends by week
   */
  static getWeeklyDurationTrends(userProgress: UserProgress | null): Array<{
    week: number;
    averageDuration: number;
    totalWorkouts: number;
    shortestWorkout: number;
    longestWorkout: number;
  }> {
    if (!userProgress) return [];

    const weeklyData: { [week: number]: number[] } = {};

    userProgress.workoutHistory
      .filter(session => session.completed && session.duration && session.duration > 0)
      .forEach(session => {
        if (!weeklyData[session.week]) {
          weeklyData[session.week] = [];
        }
        weeklyData[session.week].push(session.duration!);
      });

    return Object.entries(weeklyData)
      .map(([week, durations]) => ({
        week: parseInt(week),
        averageDuration: Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length),
        totalWorkouts: durations.length,
        shortestWorkout: Math.min(...durations),
        longestWorkout: Math.max(...durations)
      }))
      .sort((a, b) => a.week - b.week);
  }

  /**
   * Get training consistency metrics
   */
  static getTrainingConsistencyMetrics(userProgress: UserProgress | null): {
    weeklyConsistency: number;
    longestStreak: number;
    currentStreak: number;
    missedWorkouts: number;
    averageRestDays: number;
  } {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return {
        weeklyConsistency: 0,
        longestStreak: 0,
        currentStreak: 0,
        missedWorkouts: 0,
        averageRestDays: 0
      };
    }

    const completedSessions = userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Calculate streaks
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    // Calculate rest days between workouts
    const restDays: number[] = [];
    for (let i = 1; i < completedSessions.length; i++) {
      const prevDate = new Date(completedSessions[i - 1].date);
      const currentDate = new Date(completedSessions[i].date);
      const daysDiff = Math.floor((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 1) {
        restDays.push(daysDiff - 1);
      }
    }

    // Simple streak calculation based on workout frequency
    const today = new Date();
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const recentWorkouts = completedSessions.filter(session => 
      new Date(session.date) >= oneWeekAgo
    );

    currentStreak = recentWorkouts.length;
    longestStreak = Math.max(currentStreak, completedSessions.length);

    // Calculate weekly consistency
    const totalWeeks = Math.max(1, userProgress.currentWeek);
    const expectedWorkouts = totalWeeks * 5; // 5 workouts per week
    const actualWorkouts = completedSessions.length;
    const weeklyConsistency = Math.round((actualWorkouts / expectedWorkouts) * 100);

    return {
      weeklyConsistency,
      longestStreak,
      currentStreak,
      missedWorkouts: Math.max(0, expectedWorkouts - actualWorkouts),
      averageRestDays: restDays.length > 0 ? Math.round(restDays.reduce((sum, days) => sum + days, 0) / restDays.length) : 0
    };
  }

  /**
   * Get exercise variety metrics
   */
  static getExerciseVarietyMetrics(userProgress: UserProgress | null): {
    totalUniqueExercises: number;
    exercisesThisWeek: number;
    mostFrequentExercise: { name: string; count: number };
    exerciseDistribution: Array<{ exerciseName: string; frequency: number; percentage: number }>;
  } {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return {
        totalUniqueExercises: 0,
        exercisesThisWeek: 0,
        mostFrequentExercise: { name: 'None', count: 0 },
        exerciseDistribution: []
      };
    }

    const exerciseFrequency: { [exerciseName: string]: number } = {};
    const exercisesThisWeek = new Set<string>();

    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          exerciseFrequency[exercise.exerciseName] = (exerciseFrequency[exercise.exerciseName] || 0) + 1;
          
          if (session.week === userProgress.currentWeek) {
            exercisesThisWeek.add(exercise.exerciseName);
          }
        });
      });

    const totalExerciseInstances = Object.values(exerciseFrequency).reduce((sum, count) => sum + count, 0);
    
    const exerciseDistribution = Object.entries(exerciseFrequency)
      .map(([name, frequency]) => ({
        exerciseName: name,
        frequency,
        percentage: Math.round((frequency / totalExerciseInstances) * 100)
      }))
      .sort((a, b) => b.frequency - a.frequency);

    const mostFrequentExercise = exerciseDistribution.length > 0 
      ? { name: exerciseDistribution[0].exerciseName, count: exerciseDistribution[0].frequency }
      : { name: 'None', count: 0 };

    return {
      totalUniqueExercises: Object.keys(exerciseFrequency).length,
      exercisesThisWeek: exercisesThisWeek.size,
      mostFrequentExercise,
      exerciseDistribution: exerciseDistribution.slice(0, 10) // Top 10
    };
  }

  /**
   * Get all exercise improvements for sub-carousel display
   */
  static getAllExerciseImprovements(userProgress: UserProgress | null): {
    exerciseName: string;
    weightGain: number;
    startWeight: number;
    currentWeight: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }
    
    // Group exercises by name and get first and last performances
    const exerciseProgress: { [name: string]: { first: number; last: number; sessions: number } } = {};
    
    userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(session => {
        session.exercises.forEach(exercise => {
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          
          if (!exerciseProgress[exercise.exerciseName]) {
            exerciseProgress[exercise.exerciseName] = {
              first: maxWeight,
              last: maxWeight,
              sessions: 1
            };
          } else {
            exerciseProgress[exercise.exerciseName].last = maxWeight;
            exerciseProgress[exercise.exerciseName].sessions++;
          }
        });
      });
    
    // Convert to array and filter for exercises with meaningful progress
    const improvements = Object.entries(exerciseProgress)
      .filter(([_, data]) => data.sessions >= 2 && data.first > 0) // At least 2 sessions and valid starting weight
      .map(([exerciseName, data]) => ({
        exerciseName,
        weightGain: data.last - data.first,
        startWeight: data.first,
        currentWeight: data.last
      }))
      .filter(exercise => exercise.weightGain > 0) // Only show actual improvements
      .sort((a, b) => b.weightGain - a.weightGain); // Sort by highest weight gain first
    
    return improvements;
  }

  /**
   * Get week-to-week exercise improvements for sub-carousel display
   */
  static getWeekToWeekImprovements(userProgress: UserProgress | null): {
    exerciseName: string;
    weightGain: number;
    previousWeight: number;
    currentWeight: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }
    
    const currentWeek = userProgress.currentWeek;
    const previousWeek = currentWeek - 1;
    
    // If we're in week 1, there's no previous week to compare
    if (previousWeek < 1) {
      return [];
    }
    
    // Get sessions for current and previous week
    const currentWeekSessions = userProgress.workoutHistory.filter(
      session => session.week === currentWeek && session.completed
    );
    const previousWeekSessions = userProgress.workoutHistory.filter(
      session => session.week === previousWeek && session.completed
    );
    
    if (currentWeekSessions.length === 0 || previousWeekSessions.length === 0) {
      return [];
    }
    
    // Group exercises by name for each week
    const currentWeekExercises: { [name: string]: number } = {};
    const previousWeekExercises: { [name: string]: number } = {};
    
    // Get max weights for current week
    currentWeekSessions.forEach(session => {
      session.exercises.forEach(exercise => {
        const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
        if (!currentWeekExercises[exercise.exerciseName] || maxWeight > currentWeekExercises[exercise.exerciseName]) {
          currentWeekExercises[exercise.exerciseName] = maxWeight;
        }
      });
    });
    
    // Get max weights for previous week
    previousWeekSessions.forEach(session => {
      session.exercises.forEach(exercise => {
        const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
        if (!previousWeekExercises[exercise.exerciseName] || maxWeight > previousWeekExercises[exercise.exerciseName]) {
          previousWeekExercises[exercise.exerciseName] = maxWeight;
        }
      });
    });
    
    // Compare and find improvements
    const improvements = Object.entries(currentWeekExercises)
      .filter(([exerciseName, currentWeight]) => {
        const previousWeight = previousWeekExercises[exerciseName];
        return previousWeight && currentWeight > previousWeight;
      })
      .map(([exerciseName, currentWeight]) => ({
        exerciseName,
        weightGain: currentWeight - previousWeekExercises[exerciseName],
        previousWeight: previousWeekExercises[exerciseName],
        currentWeight
      }))
      .sort((a, b) => b.weightGain - a.weightGain); // Sort by highest weight gain first
    
    return improvements;
  }

  /**
   * Get week-to-week exercise improvements grouped by day for sub-carousel display
   */
  static getWeekToWeekImprovementsByDay(userProgress: UserProgress | null): {
    day: string;
    exerciseName: string;
    weightGain: number;
    previousWeight: number;
    currentWeight: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      console.log('🔍 DEBUG: No user progress or workout history');
      return [];
    }
    
    const currentWeek = userProgress.currentWeek;
    const previousWeek = currentWeek - 1;
    
    console.log(`🔍 DEBUG: Comparing Week ${previousWeek} vs Week ${currentWeek}`);
    
    // If we're in week 1, there's no previous week to compare
    if (previousWeek < 1) {
      console.log('🔍 DEBUG: In week 1, no previous week to compare');
      return [];
    }
    
    // Get sessions for current and previous week
    const currentWeekSessions = userProgress.workoutHistory.filter(
      session => session.week === currentWeek && session.completed
    );
    const previousWeekSessions = userProgress.workoutHistory.filter(
      session => session.week === previousWeek && session.completed
    );
    
    console.log(`🔍 DEBUG: Current week sessions: ${currentWeekSessions.length}`);
    console.log(`🔍 DEBUG: Previous week sessions: ${previousWeekSessions.length}`);
    
    if (currentWeekSessions.length === 0 || previousWeekSessions.length === 0) {
      console.log('🔍 DEBUG: Missing sessions for comparison');
      return [];
    }
    
    const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
    const improvementsByDay: {
      day: string;
      exerciseName: string;
      weightGain: number;
      previousWeight: number;
      currentWeight: number;
    }[] = [];
    
    // Process each workout day
    workoutDays.forEach(day => {
      const currentDaySession = currentWeekSessions.find(session => session.day === day);
      const previousDaySession = previousWeekSessions.find(session => session.day === day);
      
      console.log(`🔍 DEBUG: ${day} - Current: ${currentDaySession ? 'Found' : 'Missing'}, Previous: ${previousDaySession ? 'Found' : 'Missing'}`);
      
      if (!currentDaySession || !previousDaySession) return;
      
      console.log(`🔍 DEBUG: ${day} - Current exercises: ${currentDaySession.exercises.length}, Previous exercises: ${previousDaySession.exercises.length}`);
      
      // Compare exercises for this specific day
      currentDaySession.exercises.forEach(currentExercise => {
        const previousExercise = previousDaySession.exercises.find(
          ex => ex.exerciseName === currentExercise.exerciseName
        );
        
        if (previousExercise) {
          const currentWeight = Math.max(...currentExercise.sets.map(set => set.weight));
          const previousWeight = Math.max(...previousExercise.sets.map(set => set.weight));
          
          console.log(`🔍 DEBUG: ${day} - ${currentExercise.exerciseName}: ${previousWeight}lbs → ${currentWeight}lbs`);
          
          if (currentWeight > previousWeight) {
            const improvement = {
              day,
              exerciseName: currentExercise.exerciseName,
              weightGain: currentWeight - previousWeight,
              previousWeight,
              currentWeight
            };
            improvementsByDay.push(improvement);
            console.log(`✅ IMPROVEMENT: ${day} - ${currentExercise.exerciseName}: +${improvement.weightGain}lbs`);
          } else if (currentWeight === previousWeight) {
            console.log(`➖ NO CHANGE: ${day} - ${currentExercise.exerciseName}: ${currentWeight}lbs (same)`);
          } else {
            console.log(`📉 DECREASED: ${day} - ${currentExercise.exerciseName}: ${previousWeight}lbs → ${currentWeight}lbs`);
          }
        } else {
          console.log(`❌ NO MATCH: ${day} - ${currentExercise.exerciseName} not found in previous week`);
        }
      });
    });
    
    console.log(`🔍 DEBUG: Total improvements found: ${improvementsByDay.length}`);
    improvementsByDay.forEach(imp => {
      console.log(`  - ${imp.day}: ${imp.exerciseName} (+${imp.weightGain}lbs)`);
    });
    
    // Sort by day order, then by weight gain within each day
    const dayOrder = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
    improvementsByDay.sort((a, b) => {
      const dayComparison = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      if (dayComparison !== 0) return dayComparison;
      return b.weightGain - a.weightGain; // Higher weight gains first within the same day
    });
    
    return improvementsByDay;
  }

  /**
   * Get weekly completion data for progress charts
   */
  static getWeeklyCompletionData(userProgress: UserProgress | null): {
    week: number;
    completed: number;
    total: number;
    percentage: number;
    workoutDays: string[];
    completedDays: string[];
    skippedDays: string[];
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const totalWeeks = 12;
    const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
    const weeklyData: {
      week: number;
      completed: number;
      total: number;
      percentage: number;
      workoutDays: string[];
      completedDays: string[];
      skippedDays: string[];
    }[] = [];

    for (let week = 1; week <= totalWeeks; week++) {
      const weekSessions = userProgress.workoutHistory.filter(
        session => session.week === week
      );

      const completedSessions = weekSessions.filter(session => session.completed);
      const skippedSessions = weekSessions.filter(session => 
        !session.completed && session.notes && (session.notes.trim().toLowerCase() === 'skipped')
      );
      
      const completedDays = completedSessions.map(session => session.day);
      const skippedDays = skippedSessions.map(session => session.day);
      
      // Count both completed and skipped as "attempted"
      const attempted = completedSessions.length + skippedSessions.length;
      const total = workoutDays.length;
      const percentage = total > 0 ? Math.round((attempted / total) * 100) : 0;

      weeklyData.push({
        week,
        completed: attempted, // Now includes both completed and skipped
        total,
        percentage,
        workoutDays,
        completedDays,
        skippedDays
      });
    }

    return weeklyData;
  }

  /**
   * Get workout heatmap data for consistency visualization
   */
  static getWorkoutHeatmapData(userProgress: UserProgress | null): {
    date: string;
    day: string;
    week: number;
    completed: boolean;
    skipped: boolean;
    workoutDay: boolean;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const heatmapData: {
      date: string;
      day: string;
      week: number;
      completed: boolean;
      skipped: boolean;
      workoutDay: boolean;
    }[] = [];

    const workoutDays = ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'];
    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // Generate data for all 12 weeks
    for (let week = 1; week <= 12; week++) {
      allDays.forEach(day => {
        const isWorkoutDay = workoutDays.includes(day);
        const session = userProgress.workoutHistory.find(
          s => s.week === week && s.day === day
        );

        // Create a mock date for visualization (week-based)
        const mockDate = `2024-W${week.toString().padStart(2, '0')}-${day}`;

        heatmapData.push({
          date: mockDate,
          day,
          week,
          completed: session ? session.completed : false,
          skipped: session ? (!session.completed && !!session.notes && (session.notes.trim().toLowerCase() === 'skipped')) : false,
          workoutDay: isWorkoutDay
        });
      });
    }

    return heatmapData;
  }

  /**
   * Get weekly volume trends data
   */
  static getWeeklyVolumeData(userProgress: UserProgress | null): {
    week: number;
    totalVolume: number;
    averageVolume: number;
    workoutCount: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const weeklyVolumeData: {
      week: number;
      totalVolume: number;
      averageVolume: number;
      workoutCount: number;
    }[] = [];

    for (let week = 1; week <= 12; week++) {
      const weekSessions = userProgress.workoutHistory.filter(
        session => session.week === week && session.completed
      );

      const totalVolume = weekSessions.reduce((sum, session) => {
        return sum + session.exercises.reduce((exerciseSum, exercise) => {
          return exerciseSum + exercise.totalVolume;
        }, 0);
      }, 0);

      const workoutCount = weekSessions.length;
      const averageVolume = workoutCount > 0 ? Math.round(totalVolume / workoutCount) : 0;

      weeklyVolumeData.push({
        week,
        totalVolume,
        averageVolume,
        workoutCount
      });
    }

    return weeklyVolumeData;
  }

  /**
   * Get previous week exercise data for a specific exercise and set
   */
  static getPreviousWeekExerciseData(
    userProgress: UserProgress | null,
    currentWeek: number,
    currentDay: string,
    exerciseName: string,
    setNumber: number,
    setType: 'warmup' | 'working'
  ): { weight: number; reps: number; rpe?: RPE; hasData: boolean } {
    if (!userProgress || currentWeek <= 1) {
      return { weight: 0, reps: 0, hasData: false };
    }
    
    const previousWeek = currentWeek - 1;
    
    // Find the previous week session for the same day
    const previousWeekSession = userProgress.workoutHistory.find(
      session => session.week === previousWeek && 
                 session.day === currentDay && 
                 session.completed
    );
    
    if (!previousWeekSession) {
      return { weight: 0, reps: 0, hasData: false };
    }
    
    // Find the exercise in the previous week session
    const previousExercise = previousWeekSession.exercises.find(
      ex => ex.exerciseName === exerciseName
    );
    
    if (!previousExercise) {
      return { weight: 0, reps: 0, hasData: false };
    }
    
    // Find the exact set by setType and setNumber
    // This will only return data if the EXACT same set type and number existed in the previous week
    const matchingSet = previousExercise.sets.find(
      set => set.setType === setType && 
             set.setNumber === setNumber &&
             set.completed
    );
    
    if (!matchingSet) {
      // No matching set found - this means the previous week didn't have this exact set
      return { weight: 0, reps: 0, hasData: false };
    }
    
    return {
      weight: matchingSet.weight,
      reps: matchingSet.reps,
      rpe: matchingSet.actualRpe,
      hasData: true
    };
  }

  /**
   * Get workout summary chart data for volume trends over time
   */
  static getVolumeProgressData(userProgress: UserProgress | null): {
    week: number;
    totalVolume: number;
    averageVolume: number;
    workoutCount: number;
    cumulativeVolume: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const weeklyData: { [week: number]: { totalVolume: number; workoutCount: number } } = {};
    
    // Group sessions by week
    userProgress.workoutHistory.forEach(session => {
      if (session.completed) {
        const week = session.week;
        if (!weeklyData[week]) {
          weeklyData[week] = { totalVolume: 0, workoutCount: 0 };
        }
        
        const sessionVolume = session.exercises.reduce((sum, exercise) => sum + exercise.totalVolume, 0);
        weeklyData[week].totalVolume += sessionVolume;
        weeklyData[week].workoutCount += 1;
      }
    });

    // Convert to array and calculate cumulative volume
    let cumulativeVolume = 0;
    return Object.entries(weeklyData)
      .map(([week, data]) => {
        cumulativeVolume += data.totalVolume;
        return {
          week: parseInt(week),
          totalVolume: data.totalVolume,
          averageVolume: Math.round(data.totalVolume / data.workoutCount),
          workoutCount: data.workoutCount,
          cumulativeVolume
        };
      })
      .sort((a, b) => a.week - b.week);
  }

  /**
   * Get exercise distribution data for pie chart
   */
  static getExerciseDistributionData(userProgress: UserProgress | null): {
    exerciseName: string;
    totalVolume: number;
    percentage: number;
    workoutCount: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const exerciseData: { [exerciseName: string]: { totalVolume: number; workoutCount: number } } = {};
    
    // Aggregate data by exercise
    userProgress.workoutHistory.forEach(session => {
      if (session.completed) {
        session.exercises.forEach(exercise => {
          if (!exerciseData[exercise.exerciseName]) {
            exerciseData[exercise.exerciseName] = { totalVolume: 0, workoutCount: 0 };
          }
          exerciseData[exercise.exerciseName].totalVolume += exercise.totalVolume;
          exerciseData[exercise.exerciseName].workoutCount += 1;
        });
      }
    });

    const totalVolume = Object.values(exerciseData).reduce((sum, data) => sum + data.totalVolume, 0);
    
    return Object.entries(exerciseData)
      .map(([exerciseName, data]) => ({
        exerciseName,
        totalVolume: data.totalVolume,
        percentage: Math.round((data.totalVolume / totalVolume) * 100),
        workoutCount: data.workoutCount
      }))
      .sort((a, b) => b.totalVolume - a.totalVolume)
      .slice(0, 10); // Top 10 exercises
  }

  /**
   * Get workout frequency data by day of week
   */
  static getWorkoutFrequencyByDay(userProgress: UserProgress | null): {
    day: string;
    workoutCount: number;
    completionRate: number;
    totalScheduled: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const dayData: { [day: string]: { completed: number; total: number } } = {
      'Monday': { completed: 0, total: 0 },
      'Tuesday': { completed: 0, total: 0 },
      'Thursday': { completed: 0, total: 0 },
      'Friday': { completed: 0, total: 0 },
      'Saturday': { completed: 0, total: 0 }
    };

    // Count workouts by day
    userProgress.workoutHistory.forEach(session => {
      if (dayData[session.day]) {
        dayData[session.day].total += 1;
        if (session.completed) {
          dayData[session.day].completed += 1;
        }
      }
    });

    return Object.entries(dayData).map(([day, data]) => ({
      day,
      workoutCount: data.completed,
      completionRate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      totalScheduled: data.total
    }));
  }

  /**
   * Get monthly workout summary data
   */
  static getMonthlyWorkoutData(userProgress: UserProgress | null): {
    month: string;
    workoutCount: number;
    totalVolume: number;
    averageRPE: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const monthlyData: { [month: string]: { 
      workoutCount: number; 
      totalVolume: number; 
      totalRPE: number; 
      rpeCount: number; 
    } } = {};

    userProgress.workoutHistory.forEach(session => {
      if (session.completed) {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { workoutCount: 0, totalVolume: 0, totalRPE: 0, rpeCount: 0 };
        }
        
        monthlyData[monthKey].workoutCount += 1;
        monthlyData[monthKey].totalVolume += session.exercises.reduce((sum, ex) => sum + ex.totalVolume, 0);
        
        // Calculate average RPE for the session
        const sessionRPEs = session.exercises.flatMap(ex => 
          ex.sets.filter(set => set.rpe !== undefined).map(set => this.rpeToNumber(set.rpe!))
        );
        if (sessionRPEs.length > 0) {
          const sessionAvgRPE = sessionRPEs.reduce((sum, rpe) => sum + rpe, 0) / sessionRPEs.length;
          monthlyData[monthKey].totalRPE += sessionAvgRPE;
          monthlyData[monthKey].rpeCount += 1;
        }
      }
    });

    return Object.entries(monthlyData)
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        workoutCount: data.workoutCount,
        totalVolume: data.totalVolume,
        averageRPE: data.rpeCount > 0 ? Math.round((data.totalRPE / data.rpeCount) * 10) / 10 : 0
      }))
      .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  }

  /**
   * Get exercise progression data for most improved exercise charts
   */
  static getExerciseProgressionData(userProgress: UserProgress | null, exerciseName?: string): {
    exerciseName: string;
    progressionData: {
      week: number;
      date: string;
      maxWeight: number;
      totalVolume: number;
      averageRPE: number;
      actualRPE: number;
      rpeEfficiency: number; // actual RPE vs expected RPE
    }[];
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    // Group exercises by name
    const exerciseGroups: { [name: string]: any[] } = {};
    
    userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(session => {
        session.exercises.forEach(exercise => {
          if (!exerciseGroups[exercise.exerciseName]) {
            exerciseGroups[exercise.exerciseName] = [];
          }
          
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          const workingSets = exercise.sets.filter(set => set.setType === 'working' && set.completed);
          
          // Calculate actual RPE average for working sets
          const actualRPEs = workingSets
            .filter(set => set.actualRpe)
            .map(set => this.rpeToNumber(set.actualRpe!));
          const averageActualRPE = actualRPEs.length > 0 
            ? actualRPEs.reduce((sum, rpe) => sum + rpe, 0) / actualRPEs.length 
            : 0;

          // Calculate expected RPE average for working sets
          // We need to get this from the exercise definition, not from set.rpe
          // For now, we'll use a placeholder since we don't have access to exercise definitions here
          // This will be handled in the chart components where we have access to exercise data
          const averageExpectedRPE = 0; // Placeholder

          // RPE efficiency: how close actual RPE was to expected (lower difference = better)
          const rpeEfficiency = averageExpectedRPE > 0 && averageActualRPE > 0 
            ? Math.abs(averageExpectedRPE - averageActualRPE) 
            : 0;

          exerciseGroups[exercise.exerciseName].push({
            week: session.week,
            date: exercise.date,
            maxWeight,
            totalVolume: exercise.totalVolume,
            averageRPE: averageExpectedRPE,
            actualRPE: averageActualRPE,
            rpeEfficiency
          });
        });
      });

    // Filter for specific exercise if provided, otherwise return all
    const exercisesToInclude = exerciseName 
      ? [exerciseName] 
      : Object.keys(exerciseGroups).filter(name => exerciseGroups[name].length >= 3); // At least 3 data points

    return exercisesToInclude.map(name => ({
      exerciseName: name,
      progressionData: exerciseGroups[name]
    }));
  }

  /**
   * Get personal records timeline data
   */
  static getPersonalRecordsTimeline(userProgress: UserProgress | null): {
    date: string;
    week: number;
    exerciseName: string;
    recordType: 'weight' | 'volume';
    value: number;
    previousRecord: number;
    improvement: number;
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const records: any[] = [];
    const exerciseRecords: { [exerciseName: string]: { maxWeight: number; maxVolume: number } } = {};

    userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(session => {
        session.exercises.forEach(exercise => {
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          const currentVolume = exercise.totalVolume;

          if (!exerciseRecords[exercise.exerciseName]) {
            exerciseRecords[exercise.exerciseName] = { maxWeight: 0, maxVolume: 0 };
          }

          const previousMaxWeight = exerciseRecords[exercise.exerciseName].maxWeight;
          const previousMaxVolume = exerciseRecords[exercise.exerciseName].maxVolume;

          // Check for weight PR
          if (maxWeight > previousMaxWeight) {
            records.push({
              date: exercise.date,
              week: session.week,
              exerciseName: exercise.exerciseName,
              recordType: 'weight',
              value: maxWeight,
              previousRecord: previousMaxWeight,
              improvement: maxWeight - previousMaxWeight
            });
            exerciseRecords[exercise.exerciseName].maxWeight = maxWeight;
          }

          // Check for volume PR
          if (currentVolume > previousMaxVolume) {
            records.push({
              date: exercise.date,
              week: session.week,
              exerciseName: exercise.exerciseName,
              recordType: 'volume',
              value: currentVolume,
              previousRecord: previousMaxVolume,
              improvement: currentVolume - previousMaxVolume
            });
            exerciseRecords[exercise.exerciseName].maxVolume = currentVolume;
          }
        });
      });

    return records;
  }

  /**
   * Get RPE accuracy analysis
   */
  static getRPEAccuracyData(userProgress: UserProgress | null): {
    exerciseName: string;
    week: number;
    expectedRPE: number;
    actualRPE: number;
    difference: number;
    accuracy: number; // percentage accuracy
  }[] {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return [];
    }

    const rpeData: any[] = [];

    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          // Only look at working sets that have actual RPE recorded
          const workingSets = exercise.sets.filter(set => 
            set.setType === 'working' && 
            set.completed && 
            set.actualRpe // Only need actual RPE from database
          );

          workingSets.forEach(set => {
            // For now, we can't get expected RPE here since we don't have access to exercise definitions
            // This will be handled in the chart components where we have access to exercise data
            if (set.actualRpe) {
              const actualRPE = this.rpeToNumber(set.actualRpe);
              
              // Placeholder for expected RPE - will be filled in by chart components
              rpeData.push({
                exerciseName: exercise.exerciseName,
                week: session.week,
                expectedRPE: 0, // Placeholder
                actualRPE,
                difference: 0, // Placeholder
                accuracy: 0, // Placeholder
                setNumber: set.setNumber,
                setType: set.setType
              });
            }
          });
        });
      });

    return rpeData;
  }

  /**
   * Get top personal records summary
   */
  static getTopPersonalRecords(userProgress: UserProgress | null): {
    weightRecords: { exerciseName: string; weight: number; date: string; week: number }[];
    volumeRecords: { exerciseName: string; volume: number; date: string; week: number }[];
  } {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return { weightRecords: [], volumeRecords: [] };
    }

    const exerciseMaxes: { [exerciseName: string]: { 
      maxWeight: number; 
      maxVolume: number; 
      weightDate: string; 
      volumeDate: string;
      weightWeek: number;
      volumeWeek: number;
    } } = {};

    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
          
          if (!exerciseMaxes[exercise.exerciseName]) {
            exerciseMaxes[exercise.exerciseName] = {
              maxWeight: 0,
              maxVolume: 0,
              weightDate: '',
              volumeDate: '',
              weightWeek: 0,
              volumeWeek: 0
            };
          }

          if (maxWeight > exerciseMaxes[exercise.exerciseName].maxWeight) {
            exerciseMaxes[exercise.exerciseName].maxWeight = maxWeight;
            exerciseMaxes[exercise.exerciseName].weightDate = exercise.date;
            exerciseMaxes[exercise.exerciseName].weightWeek = session.week;
          }

          if (exercise.totalVolume > exerciseMaxes[exercise.exerciseName].maxVolume) {
            exerciseMaxes[exercise.exerciseName].maxVolume = exercise.totalVolume;
            exerciseMaxes[exercise.exerciseName].volumeDate = exercise.date;
            exerciseMaxes[exercise.exerciseName].volumeWeek = session.week;
          }
        });
      });

    const weightRecords = Object.entries(exerciseMaxes)
      .map(([exerciseName, data]) => ({
        exerciseName,
        weight: data.maxWeight,
        date: data.weightDate,
        week: data.weightWeek
      }))
      .filter(record => record.weight > 0)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 10);

    const volumeRecords = Object.entries(exerciseMaxes)
      .map(([exerciseName, data]) => ({
        exerciseName,
        volume: data.maxVolume,
        date: data.volumeDate,
        week: data.volumeWeek
      }))
      .filter(record => record.volume > 0)
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10);

    return { weightRecords, volumeRecords };
  }
} 